import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { concat, forkJoin, Subscription } from 'rxjs';
import { Router, NavigationStart } from '@angular/router';
import { filter, map, switchMap, tap, toArray } from 'rxjs/operators';
import { WorkScheduleCalendarDataService } from '../../../services/utils/work-schedule-calendar-data.service';
import { SnackBarService } from '../../../services/utils/snackBar.service';
import { DialogService } from '../../../services/utils/dialog.service';
import { DialogRepeatPatternData } from '../dialog-repeat-pattern/dialog-repeat-pattern.component';
import { DialogAssignRotationData, DialogAssignRotationResult } from '../dialog-assign-rotation/dialog-assign-rotation.component';
import { describeScheduleErrorHint } from '../../../utils/work-schedule-error.util';
import {
  RotationPattern,
  WeeklyPattern,
  ResolvedDayShift,
  CalendarDayCell,
  WeekTotal,
  ScheduleWarningApi,
  MappedSchedule,
  buildMonthWeeks,
  dateKey,
  weekRangeLabel,
  computeWeekTotals
} from '../../../utils/work-schedule-calendar.util';
import { formatHoursEs, toShortTime12h } from '../../../utils/time-format.util';
import {
  RotationAssignment,
  addDaysToDateKey,
  describeRotationWarning,
  extractRotationWarnings
} from '../../../utils/work-shift-rotation.util';

/** Espejo de $wsched-colors en dialog-work-schedule-calendar.component.scss — el diálogo de
 * repetición de patrón necesita colores reales (hex), no las clases wsched-color--{key} que usa
 * el resto de este calendario. Mantener ambos en sync si se agrega/cambia un color. */
const WSCHED_COLOR_HEX: { [key: string]: string } = {
  blue: '#039be5',
  green: '#6aca25',
  orange: '#ff7f0b',
  purple: '#7c4dff',
  teal: '#00acc1',
  indigo: '#6366f1',
  gray: '#94a3b8'
};

function colorKeyToHex(colorKey: string): string {
  return WSCHED_COLOR_HEX[colorKey] || WSCHED_COLOR_HEX.gray;
}

export interface WorkScheduleCalendarDialogData {
  personalEmployerId: string | number;
  personName: string;
  documentLabel?: string;
  positionLabel?: string;
  employerLabel?: string;
  projectLabel?: string;
  isActive?: boolean;
  projectId: string;
  adminInfoId: string;
  /** Lista de turnos ya cargada por el llamador (evita repetir GET api/WorkShift/Project/{id}). */
  workShifts?: any[];
  /** Permiso 'shiftreschedule.update' Y 'personalsemployerscontract.EditShiftScheduling' — sin ambos, el diálogo es de solo lectura. */
  canEdit: boolean;
  /** Permiso 'personalsemployerscontract.EditShiftScheduling': si false, todo el diálogo se muestra inactivo y congelado. */
  canEditShiftScheduling: boolean;
  /**
   * Permiso 'schedule.edit-past' comprobado contra claims directos del usuario.
   * Si false, ningún usuario (ni superusuario) puede programar días anteriores a hoy.
   */
  canEditPast: boolean;
  initialYear?: number;
  initialMonth?: number;
}

@Component({
  selector: 'app-dialog-work-schedule-calendar',
  templateUrl: './dialog-work-schedule-calendar.component.html',
  styleUrls: ['./dialog-work-schedule-calendar.component.scss']
})
export class DialogWorkScheduleCalendarComponent implements OnInit, OnDestroy {

  loading = true;
  scheduleLoading = false;
  saving = false;
  canEdit: boolean;
  /** Refleja data.canEditShiftScheduling: false → todo el diálogo queda congelado. */
  canEditShiftScheduling: boolean;
  /**
   * Refleja data.canEditPast: false → los días anteriores a hoy están bloqueados
   * aunque el servidor no devuelva editableFrom (nadie, ni superusuario, puede editarlos).
   */
  canEditPast: boolean;

  /** Catálogo completo del proyecto (turnos diarios + patrones semanales) — ver getters
   * weeklyPatterns/dailyShifts más abajo, igual que en "Programación masiva de horarios". */
  patterns: WeeklyPattern[] = [];
  maxWeeklyHours = 0;

  year: number;
  month: number; // 1-12
  weeks: CalendarDayCell[][] = [];
  assignments = new Map<string, ResolvedDayShift | null>();
  weekTotals: WeekTotal[] = [];

  /** Primer día editable devuelto por el servidor; null = sin tope (usuario con schedule.edit-past). */
  editableFrom: Date | null = null;
  warnings: ScheduleWarningApi[] = [];
  /** Fechas devueltas por un 409 de negocio, para resaltarlas en la grilla. */
  conflictDates = new Set<string>();
  /** Motivo del último 409 (mensaje del backend + traducción de describeScheduleErrorHint) —
   * el PUT/DELETE individual rechaza el rango entero con UN solo code/message, así que aplica
   * igual a todas las fechas en conflictDates (a diferencia de la masiva, que sí distingue
   * motivo por persona/día). */
  conflictReason = '';

  /** Rotaciones (patrones catorcenales) activas del proyecto, para el sidebar. */
  rotationPatterns: RotationPattern[] = [];
  /** Asignaciones de rotación del contrato que tocan el mes visible (raíz `Rotations` del GET). */
  rotations: RotationAssignment[] = [];
  /** Hay ajustes locales (días arrastrados/quitados) sin guardar en el mes visible. */
  hasUnsavedChanges = false;
  /** Fechas ("YYYY-MM-DD") que el usuario modificó (asignó o quitó) y aún no se guardan: es lo
   * único que se envía en el PUT, nunca el mes completo. */
  private dirtyDates = new Set<string>();

  selectedPatternId: string | null = null;
  private draggingPatternId: string | null = null;
  private routerSub: Subscription;

  readonly monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  readonly weekdayHeaders = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'DOMINGO'];

  constructor(
    public dialogRef: MatDialogRef<DialogWorkScheduleCalendarComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WorkScheduleCalendarDialogData,
    private calendarDataService: WorkScheduleCalendarDataService,
    private snackBService: SnackBarService,
    private dialogService: DialogService,
    private router: Router
  ) {
    const now = new Date();
    this.year = data.initialYear ?? now.getFullYear();
    this.month = data.initialMonth ?? (now.getMonth() + 1);
    this.canEditShiftScheduling = !!data.canEditShiftScheduling;
    this.canEditPast = !!data.canEditPast;
    // canEdit ya viene calculado desde el llamador (shiftreschedule.update && EditShiftScheduling)
    this.canEdit = !!data.canEdit;

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => {
      setTimeout(() => this.dialogRef.close(), 0);
    });
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.loading = true;
    forkJoin({
      patterns: this.calendarDataService.getWeeklyPatterns(this.data.projectId, this.data.workShifts),
      maxHours: this.calendarDataService.getMaxWeeklyHours(this.data.adminInfoId)
    }).pipe(
      // Las rotaciones se pintan con el color/horas de los turnos, así que van después de cargarlos.
      switchMap(({ patterns, maxHours }) =>
        this.calendarDataService.getRotationPatterns(this.data.projectId, patterns).pipe(
          map(rotationPatterns => ({ patterns, maxHours, rotationPatterns }))
        ))
    ).subscribe({
      next: ({ patterns, maxHours, rotationPatterns }) => {
        this.patterns = patterns;
        this.rotationPatterns = rotationPatterns;
        this.maxWeeklyHours = maxHours;
        this.rebuildMonth();
        this.fetchSchedule(() => { this.loading = false; });
      },
      error: () => { this.loading = false; }
    });
  }

  /** Patrones semanales de 7 días — tarjetas de "PATRONES" en el sidebar. */
  get weeklyPatterns(): WeeklyPattern[] {
    return this.patterns.filter(p => !p.isDaily);
  }

  /** Turnos diarios (Daily.IsDaily) — chips sueltos de "TURNOS" en el sidebar, igual que en
   * "Programación masiva de horarios". */
  get dailyShifts(): WeeklyPattern[] {
    return this.patterns.filter(p => p.isDaily);
  }

  get initials(): string {
    const parts = (this.data.personName || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) { return ''; }
    const first = parts[0][0] || '';
    const second = parts.length > 1 ? parts[1][0] : '';
    return (first + second).toUpperCase();
  }

  get monthLabel(): string {
    return `${this.monthNames[this.month - 1]} de ${this.year}`;
  }

  get scheduledDaysCount(): number {
    let count = 0;
    this.assignments.forEach(shift => { if (shift && !shift.isRest) { count++; } });
    return count;
  }

  get totalMonthHours(): number {
    let total = 0;
    this.assignments.forEach(shift => { if (shift) { total += shift.durationHours; } });
    return Math.round(total * 10) / 10;
  }

  weekRange(week: CalendarDayCell[]): string {
    return weekRangeLabel(week);
  }

  formatHours(hours: number): string {
    return formatHoursEs(hours);
  }

  /** Chip de "TURNOS" en el sidebar: en vez del total de horas de la semana, muestra el horario
   * real (ej. "07:00 AM – 05:00 PM"), tomado del primer día no-descanso del turno — igual que en
   * "Programación masiva de horarios". */
  getShiftTimeRangeLabel(shift: WeeklyPattern): string {
    const scheduledDay = shift.days.find(day => day.shift && !day.shift.isRest);
    if (!scheduledDay?.shift) { return ''; }
    const [start, end] = scheduledDay.shift.timeRangeLabel.split(' – ');
    if (!start || !end) { return ''; }
    return `${toShortTime12h(start)} – ${toShortTime12h(end)}`;
  }

  /** true si el día está bloqueado para edición:
   * - Siempre: si es anterior a `editableFrom` (tope devuelto por el servidor).
   * - También: si `canEditPast` es false y el día es anterior a hoy (independientemente
   *   de lo que responda el servidor; nadie, ni superusuario, puede editar días pasados). */
  isCellLocked(cell: CalendarDayCell): boolean {
    return !!cell.date && this.isDateLocked(cell.date);
  }

  /** Mismo candado que isCellLocked, pero por fecha directa — lo necesita
   * applyPatternForWeeks para semanas que caen fuera de la grilla del mes visible. */
  private isDateLocked(date: Date): boolean {
    // Bloqueo por tope del servidor
    if (this.editableFrom && date < this.editableFrom) { return true; }
    // Bloqueo por ausencia del permiso schedule.edit-past: ningún usuario puede editar
    // días ya transcurridos aunque el servidor no haya indicado editableFrom.
    if (!this.canEditPast) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) { return true; }
    }
    return false;
  }

  isCellConflict(cell: CalendarDayCell): boolean {
    return !!cell.date && this.conflictDates.has(dateKey(cell.date));
  }

  getCellConflictReason(cell: CalendarDayCell): string {
    return this.isCellConflict(cell) ? this.conflictReason : '';
  }

  /** Día en-mes, dentro del rango cargado, sin turno vigente asignado — IsUnscheduled implícito.
   * Se muestra aunque el día esté bloqueado (pasado): el impacto en prenómina aplica igual. */
  isCellUnscheduled(cell: CalendarDayCell): boolean {
    return !!cell.date && cell.inMonth && !this.assignments.get(dateKey(cell.date));
  }

  private rebuildMonth(): void {
    this.weeks = buildMonthWeeks(this.year, this.month);
  }

  /** Primer y último día realmente pintados en la grilla — incluye, además del mes calendario,
   * los días del mes adyacente que completan la primera y la última semana (lunes/domingo). */
  private gridRange(): { from: Date; to: Date } {
    const firstCell = this.weeks[0]?.[0];
    const lastWeek = this.weeks[this.weeks.length - 1];
    const lastCell = lastWeek?.[lastWeek.length - 1];
    return {
      from: firstCell?.date || new Date(this.year, this.month - 1, 1),
      to: lastCell?.date || new Date(this.year, this.month, 0)
    };
  }

  /** Rango completo de la grilla visible, sin recortar — se usa para el GET, que sí debe traer
   * también los días bloqueados (para pintarlos con candado). */
  private monthRange(): { from: string; to: string } {
    const { from, to } = this.gridRange();
    return { from: dateKey(from), to: dateKey(to) };
  }

  /** Rango que realmente se puede escribir: recorta `from` a `editableFrom` si la grilla empieza
   * antes del tope, para no mandar nunca días bloqueados en el PUT/DELETE (el backend los
   * rechazaría igual con 409 PAST_DAYS_NOT_ALLOWED, pero total: nada se guarda, ni lo editable).
   * Devuelve null si toda la grilla cae antes de `editableFrom` (nada que enviar). */
  private editableMonthRange(): { from: string; to: string } | null {
    const { from: gridStart, to: gridEnd } = this.gridRange();
    const from = this.editableFrom && this.editableFrom > gridStart ? this.editableFrom : gridStart;
    if (from > gridEnd) { return null; }
    return { from: dateKey(from), to: dateKey(gridEnd) };
  }

  private fetchSchedule(onDone?: () => void): void {
    this.scheduleLoading = true;
    const { from, to } = this.monthRange();
    this.calendarDataService.getSchedule(this.data.personalEmployerId, from, to, this.patterns).subscribe({
      next: (mapped) => {
        this.scheduleLoading = false;
        this.applyMappedSchedule(mapped);
        onDone?.();
      },
      error: (err: HttpErrorResponse) => {
        this.scheduleLoading = false;
        this.snackBService.openError(this.extractErrorMessage(err));
        onDone?.();
      }
    });
  }

  private applyMappedSchedule(mapped: MappedSchedule): void {
    this.assignments = mapped.assignments;
    this.editableFrom = mapped.editableFrom;
    this.warnings = mapped.warnings;
    this.rotations = mapped.rotations;
    this.hasUnsavedChanges = false;
    this.dirtyDates.clear();
    this.conflictDates.clear();
    this.conflictReason = '';
    this.recomputeTotals();
  }

  /** save()/clearMonth() piden sólo el sub-rango editable del mes (recortado por editableFrom),
   * así que su respuesta no trae los días bloqueados que ya estaban pintados desde el GET —
   * hay que fusionarla dentro de ese rango en vez de reemplazar todo el mapa de asignaciones,
   * o se perdería lo que se veía en los días bloqueados. */
  private mergeMappedScheduleIntoRange(mapped: MappedSchedule, range: { from: string; to: string }): void {
    const merged = new Map(this.assignments);
    Array.from(merged.keys()).forEach(key => {
      if (key >= range.from && key <= range.to) { merged.delete(key); }
    });
    mapped.assignments.forEach((shift, key) => merged.set(key, shift));
    this.assignments = merged;
    this.editableFrom = mapped.editableFrom;
    this.warnings = mapped.warnings;
    // La respuesta de un sub-rango solo trae las asignaciones que lo tocan: se funden con las ya
    // conocidas del mes visible en vez de reemplazarlas.
    const byId = new Map(this.rotations.map(r => [r.id, r] as [number, RotationAssignment]));
    mapped.rotations.forEach(r => byId.set(r.id, r));
    this.rotations = Array.from(byId.values());
    this.conflictDates.clear();
    this.conflictReason = '';
    this.recomputeTotals();
  }

  private recomputeTotals(): void {
    this.weekTotals = computeWeekTotals(this.weeks, this.assignments, this.maxWeeklyHours);
  }

  previousMonth(): void {
    if (this.scheduleLoading) { return; }
    this.month--;
    if (this.month < 1) { this.month = 12; this.year--; }
    this.rebuildMonth();
    this.fetchSchedule();
  }

  nextMonth(): void {
    if (this.scheduleLoading) { return; }
    this.month++;
    if (this.month > 12) { this.month = 1; this.year++; }
    this.rebuildMonth();
    this.fetchSchedule();
  }

  selectPattern(patternId: string): void {
    if (!this.canEdit) { return; }
    this.selectedPatternId = this.selectedPatternId === patternId ? null : patternId;
  }

  isPatternSelected(patternId: string): boolean {
    return this.selectedPatternId === patternId;
  }

  getAssignment(cell: CalendarDayCell): ResolvedDayShift | null {
    if (!cell.date) { return null; }
    return this.assignments.get(dateKey(cell.date)) || null;
  }

  /** Tap-seleccionar-patrón → tap-en-día: aplica de una vez solo esa jornada, igual que soltar
   * un patrón sobre una celda en "Programación masiva de horarios". */
  assignToCell(cell: CalendarDayCell, weekIndex: number, columnIndex: number): void {
    if (!cell.date || !this.selectedPatternId || !this.canEdit || this.isCellLocked(cell)) { return; }
    const rotation = this.findRotationPattern(this.selectedPatternId);
    if (rotation) {
      this.promptAndAssignRotation(rotation, cell.date);
      return;
    }
    this.applyPatternToCell(cell.date, weekIndex, columnIndex, this.selectedPatternId);
  }

  /** Tap-seleccionar-patrón → tap-en-etiqueta-de-semana: pregunta cuántas semanas repetir el
   * patrón completo antes de aplicarlo (ver promptAndApplyPatternRepeat). */
  assignToWeekLabel(weekIndex: number): void {
    if (!this.selectedPatternId || !this.canEdit) { return; }
    const rotation = this.findRotationPattern(this.selectedPatternId);
    if (rotation) {
      this.promptAndAssignRotation(rotation, this.firstEditableDateOfWeek(weekIndex));
      return;
    }
    this.promptAndApplyPatternRepeat(this.selectedPatternId, weekIndex);
  }

  removeAssignment(cell: CalendarDayCell, weekIndex: number, event?: Event): void {
    event?.stopPropagation();
    if (!cell.date || !this.canEdit || this.isCellLocked(cell)) { return; }
    this.assignments.delete(dateKey(cell.date));
    this.dirtyDates.add(dateKey(cell.date));
    this.hasUnsavedChanges = true;
    this.recomputeTotals();
  }

  private applyPatternToCell(date: Date, weekIndex: number, columnIndex: number, patternId: string): void {
    const pattern = this.patterns.find(p => p.id === patternId);
    if (!pattern) { return; }
    const patternDay = pattern.days[columnIndex];
    if (!patternDay.shift) { return; }
    // Copia: el objeto del patrón se comparte entre todos los días donde se suelta.
    this.assignments.set(dateKey(date), { ...patternDay.shift, source: 'INDIVIDUAL' });
    this.dirtyDates.add(dateKey(date));
    this.hasUnsavedChanges = true;
    this.recomputeTotals();
  }

  /** Arrastrar/tocar un patrón sobre la etiqueta de una semana pregunta cuántas semanas
   * repetirlo — igual que en "Programación masiva de horarios" (a diferencia de soltar sobre un
   * día suelto, que sigue siendo instantáneo porque ahí solo se asigna esa jornada). */
  private promptAndApplyPatternRepeat(patternId: string, weekIndex: number): void {
    const pattern = this.patterns.find(p => p.id === patternId);
    const week = this.weeks[weekIndex];
    const weekStartDate = week?.[0]?.date;
    if (!pattern || !weekStartDate) { return; }

    const dialogData: DialogRepeatPatternData = {
      patternName: pattern.name,
      patternColor: colorKeyToHex(pattern.colorKey),
      days: pattern.days.map(day => ({ letter: day.letter, color: colorKeyToHex(day.colorKey) })),
      weekStartDate,
      weekRangeLabel: this.weekRange(week)
    };

    this.dialogService.openDialogRepeatPattern(dialogData).afterClosed().subscribe((weeks: number | false) => {
      if (!weeks) { return; }
      this.applyPatternForWeeks(pattern, weekStartDate, weeks);
    });
  }

  /** "Aplicar" del diálogo de repetición guarda de una vez en el backend — a diferencia del
   * resto de la edición (borrador hasta que se pulsa "Guardar"), porque las semanas siguientes a
   * la visible no están cargadas en pantalla y no hay forma de dejarlas "pendientes". */
  private applyPatternForWeeks(pattern: WeeklyPattern, weekStartDate: Date, weeks: number): void {
    const spanDates: Date[] = [];
    for (let offset = 0; offset < weeks * 7; offset++) {
      const date = new Date(weekStartDate);
      date.setDate(date.getDate() + offset);
      spanDates.push(date);
    }
    const rangeEnd = spanDates[spanDates.length - 1];
    const editableDates = spanDates.filter(date => !this.isDateLocked(date));
    if (!editableDates.length) {
      this.snackBService.openWarning('Todo el rango seleccionado está bloqueado; no hay nada que aplicar.');
      return;
    }
    const from = editableDates[0];
    const days = editableDates.map(date => ({ date: dateKey(date), workShiftId: pattern.id }));

    this.saving = true;
    this.calendarDataService.saveSchedule(this.data.personalEmployerId, dateKey(from), dateKey(rangeEnd), days, this.patterns).subscribe({
      next: (mapped) => {
        this.saving = false;
        this.mergeMappedScheduleIntoRange(mapped, { from: dateKey(from), to: dateKey(rangeEnd) });
        this.snackBService.openSuccess('Programación guardada correctamente');
      },
      error: (err: HttpErrorResponse) => this.handleScheduleError(err)
    });
  }

  // --- Drag & drop nativo (mejora de escritorio; en pantallas táctiles se usa
  // tap-seleccionar-patrón → tap-en-día/semana, que funciona igual en cualquier tamaño de pantalla) ---
  isDraggingPattern = false;
  private dragOverCellKey: string | null = null;
  private dragOverWeekIndex: number | null = null;

  onDragStartPattern(patternId: string, event: DragEvent): void {
    if (!this.canEdit) { return; }
    this.draggingPatternId = patternId;
    this.isDraggingPattern = true;
    event.dataTransfer?.setData('text/plain', patternId);
  }

  onDragEndPattern(): void {
    this.draggingPatternId = null;
    this.isDraggingPattern = false;
    this.dragOverCellKey = null;
    this.dragOverWeekIndex = null;
  }

  isCellDragOver(cell: CalendarDayCell): boolean {
    return !!cell.date && this.dragOverCellKey === dateKey(cell.date);
  }

  isWeekDragOver(weekIndex: number): boolean {
    return this.dragOverWeekIndex === weekIndex;
  }

  onCellDragOver(cell: CalendarDayCell, event: DragEvent): void {
    if (!this.canEdit || this.isCellLocked(cell)) { return; }
    event.preventDefault();
    if (cell.date) { this.dragOverCellKey = dateKey(cell.date); }
  }

  onCellDragLeave(cell: CalendarDayCell): void {
    if (!cell.date || this.dragOverCellKey === dateKey(cell.date)) { this.dragOverCellKey = null; }
  }

  onCellDrop(cell: CalendarDayCell, weekIndex: number, columnIndex: number, event: DragEvent): void {
    event.preventDefault();
    this.dragOverCellKey = null;
    const patternId = event.dataTransfer?.getData('text/plain') || this.draggingPatternId;
    this.draggingPatternId = null;
    if (!cell.date || !patternId || !this.canEdit || this.isCellLocked(cell)) { return; }
    const rotation = this.findRotationPattern(patternId);
    if (rotation) {
      this.promptAndAssignRotation(rotation, cell.date);
      return;
    }
    this.applyPatternToCell(cell.date, weekIndex, columnIndex, patternId);
  }

  onWeekDragOver(weekIndex: number, event: DragEvent): void {
    if (!this.canEdit) { return; }
    event.preventDefault();
    this.dragOverWeekIndex = weekIndex;
  }

  onWeekDragLeave(weekIndex: number): void {
    if (this.dragOverWeekIndex === weekIndex) { this.dragOverWeekIndex = null; }
  }

  onWeekLabelDrop(weekIndex: number, event: DragEvent): void {
    event.preventDefault();
    this.dragOverWeekIndex = null;
    const patternId = event.dataTransfer?.getData('text/plain') || this.draggingPatternId;
    this.draggingPatternId = null;
    if (!patternId || !this.canEdit) { return; }
    const rotation = this.findRotationPattern(patternId);
    if (rotation) {
      this.promptAndAssignRotation(rotation, this.firstEditableDateOfWeek(weekIndex));
      return;
    }
    this.promptAndApplyPatternRepeat(patternId, weekIndex);
  }

  clearMonth(): void {
    if (!this.canEdit || this.saving) { return; }
    const range = this.editableMonthRange();
    if (!range) {
      this.snackBService.openWarning('Todo este mes está bloqueado; no hay nada que limpiar.');
      return;
    }
    // Con un patrón asignado, limpiar solo borra los ajustes individuales: los días vuelven al patrón
    // (no existe "dejar el día sin turno a propósito").
    const hasRotation = this.rotations.length > 0;
    this.dialogService.openDialogConfirm(
      hasRotation
        ? '¿Deseas limpiar los ajustes individuales editables de este mes? Los días volverán a seguir el patrón asignado.'
        : '¿Deseas limpiar la programación editable de este mes? Los días quedarán sin turno.',
      true, true, 'CANCELAR', 'LIMPIAR'
    ).afterClosed().subscribe(confirmed => {
      if (!confirmed || confirmed.confirm === false) { return; }
      this.saving = true;
      this.calendarDataService.clearSchedule(this.data.personalEmployerId, range.from, range.to, this.patterns).subscribe({
        next: () => {
          this.saving = false;
          if (hasRotation) {
            // Los días que tenían ajuste vuelven a la rotación: hay que pedir el mes de nuevo.
            this.fetchSchedule();
          } else {
            this.clearAssignmentsInRange(range);
          }
          this.snackBService.openSuccess('Programación del mes eliminada');
        },
        error: (err: HttpErrorResponse) => this.handleScheduleError(err)
      });
    });
  }

  /** After a successful DELETE, remove all local assignments in the editable range
   * instead of relying on the server response (which may return stale data). */
  private clearAssignmentsInRange(range: { from: string; to: string }): void {
    const cleaned = new Map(this.assignments);
    Array.from(cleaned.keys()).forEach(key => {
      if (key >= range.from && key <= range.to) { cleaned.delete(key); }
    });
    this.assignments = cleaned;
    this.hasUnsavedChanges = false;
    this.dirtyDates.clear();
    this.conflictDates.clear();
    this.conflictReason = '';
    this.recomputeTotals();
  }

  save(): void {
    if (!this.canEdit || this.saving) { return; }
    const range = this.editableMonthRange();
    if (!range) {
      this.snackBService.openWarning('Todo este mes está bloqueado; no hay nada que guardar.');
      return;
    }
    // El PUT reemplaza el rango completo (from..to): un día ausente pierde su ajuste individual. Por eso
    // NO se manda todo el mes — los días ya aprobados (p. ej. con horas extras) harían que el backend
    // rechace el guardado completo. Solo se mandan los días que el usuario modificó, agrupados en
    // tramos de fechas consecutivas, de modo que cada PUT/DELETE toca únicamente esos días.
    // Los días bloqueados (antes de editableFrom) nunca se incluyen. Un día que solo viene heredado
    // de la rotación (source = ROTATION) no se manda como ajuste: enviarlo lo "congelaría".
    const dirty = Array.from(this.dirtyDates)
      .filter(date => date >= range.from && date <= range.to)
      .sort();
    if (!dirty.length) {
      this.dirtyDates.clear();
      this.hasUnsavedChanges = false;
      return;
    }

    const requests = this.groupConsecutiveDates(dirty).map(run => {
      const from = run[0];
      const to = run[run.length - 1];
      const days = run
        .map(date => ({ date, shift: this.assignments.get(date) }))
        .filter(({ shift }) => !!shift && shift.source !== 'ROTATION' && !!shift.patternId)
        .map(({ date, shift }) => ({ date, workShiftId: shift.patternId }));
      // Tramo donde solo se quitaron días: DELETE (mismo endpoint que "limpiar mes") en vez de un PUT vacío.
      const request$ = days.length
        ? this.calendarDataService.saveSchedule(this.data.personalEmployerId, from, to, days, this.patterns)
        : this.calendarDataService.clearSchedule(this.data.personalEmployerId, from, to, this.patterns);
      // Lo ya guardado deja de estar pendiente aunque un tramo posterior falle.
      return request$.pipe(tap(() => run.forEach(date => this.dirtyDates.delete(date))));
    });

    this.saving = true;
    concat(...requests).pipe(toArray()).subscribe({
      next: () => {
        // Se recarga el mes para pintar lo que realmente quedó (rotación, aprobados, etc.).
        this.fetchSchedule(() => {
          this.saving = false;
          this.snackBService.openSuccess('Programación guardada correctamente');
          const unscheduled = this.warnings.find(w => w.Code === 'UNSCHEDULED_DAYS');
          if (unscheduled) { this.snackBService.openWarning(unscheduled.Message || 'Hay días sin turno vigente este mes'); }
        });
      },
      error: (err: HttpErrorResponse) => this.handleScheduleError(err)
    });
  }

  /** Agrupa fechas "YYYY-MM-DD" ya ordenadas en tramos de días consecutivos. */
  private groupConsecutiveDates(sortedDates: string[]): string[][] {
    const runs: string[][] = [];
    sortedDates.forEach(date => {
      const current = runs[runs.length - 1];
      if (current && addDaysToDateKey(current[current.length - 1], 1) === date) {
        current.push(date);
      } else {
        runs.push([date]);
      }
    });
    return runs;
  }

  // ---------- Patrones catorcenales (rotaciones) ----------

  private findRotationPattern(patternId: string): RotationPattern | undefined {
    return this.rotationPatterns.find(r => r.id === patternId);
  }

  isRotationPattern(patternId: string): boolean {
    return !!this.findRotationPattern(patternId);
  }

  /** Primer día de la semana (fila) que se puede editar; si toda está bloqueada, el lunes. */
  private firstEditableDateOfWeek(weekIndex: number): Date {
    const week = this.weeks[weekIndex] || [];
    const editable = week.find(cell => !!cell.date && !this.isDateLocked(cell.date));
    return (editable?.date || week[0]?.date) as Date;
  }

  /** Primera fecha en la que se puede escribir (tope del servidor y, sin schedule.edit-past, hoy). */
  private minEditableDateKey(): string | null {
    const candidates: Date[] = [];
    if (this.editableFrom) { candidates.push(this.editableFrom); }
    if (!this.canEditPast) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      candidates.push(today);
    }
    if (!candidates.length) { return null; }
    return dateKey(candidates.reduce((latest, d) => (d > latest ? d : latest)));
  }

  /** Marca de origen de una celda: el día viene de la rotación (no es un ajuste manual). */
  isRotationDay(assigned: ResolvedDayShift | null): boolean {
    return !!assigned && assigned.source === 'ROTATION';
  }

  rotationDayTooltip(assigned: ResolvedDayShift): string {
    const cycleDay = assigned.cycleDay !== undefined ? ` · día ${assigned.cycleDay + 1} del ciclo` : '';
    return `Patrón «${assigned.rotationName || 'catorcenal'}»${cycleDay}`;
  }

  rotationRangeLabel(rotation: RotationAssignment): string {
    return rotation.endDate ? `hasta el ${this.shortDateLabel(rotation.endDate)}` : 'indefinida';
  }

  shortDateLabel(key: string): string {
    const [y, m, d] = key.split('-').map(Number);
    return `${d} ${this.monthNames[m - 1].substring(0, 3).toLowerCase()} ${y}`;
  }

  /** Asignar/terminar una rotación se guarda de una vez en el backend y recarga el mes: lo local sin
   * guardar se perdería, así que antes se pide confirmación. */
  private confirmDiscardUnsaved(then: () => void): void {
    if (!this.hasUnsavedChanges) { then(); return; }
    this.dialogService.openDialogConfirm(
      'Hay cambios sin guardar en este mes. Al asignar o terminar el patrón se descartarán. ¿Deseas continuar?',
      true, true, 'CANCELAR', 'CONTINUAR'
    ).afterClosed().subscribe(confirmed => {
      if (!confirmed || confirmed.confirm === false) { return; }
      then();
    });
  }

  private promptAndAssignRotation(rotation: RotationPattern, startDate: Date): void {
    this.confirmDiscardUnsaved(() => {
      const dialogData: DialogAssignRotationData = {
        mode: 'assign',
        rotationName: rotation.name,
        startDate: dateKey(startDate),
        minDate: this.minEditableDateKey(),
        cycleLengthDays: rotation.cycleLengthDays,
        patternEndDate: rotation.endDate,
        days: rotation.days.map(day => ({ letter: day.letter, color: colorKeyToHex(day.colorKey) }))
      };
      this.dialogService.openDialogAssignRotation(dialogData).afterClosed()
        .subscribe((result: DialogAssignRotationResult | false) => {
          if (!result) { return; }
          this.saving = true;
          this.calendarDataService.assignRotation(this.data.personalEmployerId, {
            workShiftRotationId: rotation.id,
            startDate: result.startDate,
            endDate: result.endDate
          }).subscribe({
            next: (resp) => this.afterRotationChange(resp, 'Patrón asignado correctamente'),
            error: (err: HttpErrorResponse) => this.handleScheduleError(err)
          });
        });
    });
  }

  endRotation(assignment: RotationAssignment): void {
    if (!this.canEdit || this.saving) { return; }
    this.confirmDiscardUnsaved(() => {
      const today = dateKey(new Date());
      const min = this.minEditableDateKey();
      const dialogData: DialogAssignRotationData = {
        mode: 'end',
        rotationName: assignment.name,
        startDate: min && min > today ? min : today,
        minDate: min
      };
      this.dialogService.openDialogAssignRotation(dialogData).afterClosed()
        .subscribe((result: DialogAssignRotationResult | false) => {
          if (!result) { return; }
          this.saving = true;
          this.calendarDataService.endRotation(this.data.personalEmployerId, result.startDate).subscribe({
            next: (resp) => this.afterRotationChange(resp, 'Patrón terminado correctamente'),
            error: (err: HttpErrorResponse) => this.handleScheduleError(err)
          });
        });
    });
  }

  private afterRotationChange(resp: any, successMessage: string): void {
    this.saving = false;
    this.selectedPatternId = null;
    const notes = extractRotationWarnings(resp).map(w => describeRotationWarning(w));
    if (notes.length) {
      this.snackBService.openWarning(`${successMessage}. ${notes.join(' ')}`);
    } else {
      this.snackBService.openSuccess(successMessage);
    }
    // La respuesta trae el mes de la fecha de inicio, que no es necesariamente el visible.
    this.fetchSchedule();
  }

  /** Cubre los 3 formatos de error del doc: 409 negocio (camelCase), resto (PascalCase `Error`). */
  private extractErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 409 && err.error?.message) {
      return err.error.message + describeScheduleErrorHint(err.error.code);
    }
    return err.error?.Error || err.error?.Message || 'Ocurrió un error al procesar la programación';
  }

  private handleScheduleError(err: HttpErrorResponse): void {
    this.saving = false;
    if (err.status === 409 && err.error?.code) {
      this.conflictDates = new Set<string>((err.error.dates || []).map((d: string) => d.substring(0, 10)));
      this.conflictReason = (err.error.message || '') + describeScheduleErrorHint(err.error.code);
    }
    this.snackBService.openError(this.extractErrorMessage(err));
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
